import React, { useCallback, useEffect, useRef, useState } from 'react'
import { errorMessageOf, wasShown } from './error-notice'
import { ErrorAlert } from './error-alert'

// A failed save on a Form or FormNew page (owner 2026-10-07). A page's
// onSubmit that lets its API call's failure through -- `await api.put(...)`
// and then navigate, the way most of these pages are written -- used to end
// in an unhandled rejection: nothing shown, the person left wondering. The
// form now catches it and shows the server's message and the failed request's
// reference (ErrorAlert, the same alert FormSimple's failures get), and the
// page stays where it is: the navigation after the await never runs, the
// person's input is untouched and the form is editable again.
//
// A page that catches its own failure (and shows it) is left alone: nothing
// reaches the form. A failure a notification already showed (the old
// useApiFormState's modal) is not shown twice (error-notice's markShown).

export const SAVE_FAILED = 'Something went wrong. Please try again.'

// An API layer's failure (api-new's response, axios's error, api.service's
// { error, status }), as opposed to an exception in the page's own code.
const isApiFailure = err => !!err && typeof err === 'object' && ['status', 'response', 'data', 'error'].some(key => key in err)

// The services' catch-all body ({ message: 'an error occured' }, core's
// MessageDefault) says nothing the plain sentence doesn't.
const GENERIC = /^an? error occu?rr?ed\.?$/i

// The server's message when the failure carries one worth reading; a page's
// own exception never shows its internals.
export const failureMessage = err => {
  if (!isApiFailure(err)) return SAVE_FAILED
  const message = errorMessageOf(err, SAVE_FAILED)
  return GENERIC.test(String(message).trim()) ? SAVE_FAILED : message
}

// [failure, submit, dismiss]: submit(...) runs onSubmit and keeps what it
// threw ({ error }, so a failure with no response -- api-new throws undefined
// for a network failure -- still shows); the next submit clears it.
export const useSaveFailure = onSubmit => {
  const [failure, setFailure] = useState(null)

  const submit = async (...args) => {
    setFailure(null)
    try {
      return onSubmit ? await onSubmit(...args) : undefined
    } catch (error) {
      if (wasShown(error)) return undefined
      // api-new throws undefined for a network failure; anything else that
      // isn't an API failure is a bug in the page, worth its stack.
      if (error !== undefined && error !== null && !isApiFailure(error)) console.error(error)
      setFailure({ error })
      return undefined
    }
  }

  const dismiss = useCallback(() => setFailure(null), [])

  return [failure, submit, dismiss]
}

// The alert, brought into view: it sits under the form's buttons, which a
// long form may have scrolled away from.
export const SaveFailure = ({ failure, onDismiss }) => {
  const ref = useRef(null)

  useEffect(
    () => {
      if (failure && ref.current && typeof ref.current.scrollIntoView === 'function') ref.current.scrollIntoView({ block: 'nearest' })
    },
    [failure]
  )

  if (!failure) return null

  return <ErrorAlert ref={ref} className="mt-2" message={failureMessage(failure.error)} error={failure.error} onClose={onDismiss} />
}
